import {useCallback, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import axios from 'axios'
import {Modal} from 'react-bootstrap'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {PageTitle} from '../../../_metronic/layout/core'
import {KTCardBody} from '../../../_metronic/helpers'
import {apiUrl} from '../../../api-utils/constantVariables'
import {AdminAuth} from '../../modules/auth/core/AdminAuth'
import {useCan} from '../../modules/auth/core/AdminPermissions'

type RoleRow = {
  id: number
  code: string
  name: string
  description?: string | null
  is_system?: boolean
}

const slugSchema = Yup.object({
  code: Yup.string()
    .matches(/^[a-z][a-z0-9_]{1,62}$/, 'Lowercase slug: letter, then letters/digits/_')
    .required(),
  name: Yup.string().min(2).max(120).required(),
  description: Yup.string().max(500),
})

const RolesWrapper = () => {
  const nav = useNavigate()
  const canCreate = useCan('roles.create')
  const canEdit = useCan('roles.edit')
  const [rows, setRows] = useState<RoleRow[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    const token = AdminAuth()
    if (!token) return
    setLoading(true)
    try {
      const res = await axios.get(`${apiUrl}/admin/roles`, {
        headers: {'x-access-token': token},
      })
      const data = res.data?.data as RoleRow[] | undefined
      setRows(Array.isArray(data) ? data : [])
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const formik = useFormik({
    initialValues: {code: '', name: '', description: ''},
    validationSchema: slugSchema,
    onSubmit: async (vals, {setSubmitting, resetForm}) => {
      setErr('')
      const token = AdminAuth()
      if (!token) return
      try {
        const res = await axios.post(
          `${apiUrl}/admin/roles`,
          {
            code: vals.code.trim().toLowerCase(),
            name: vals.name.trim(),
            description: vals.description.trim() || undefined,
          },
          {headers: {'x-access-token': token}}
        )
        const created = res.data?.data as RoleRow | undefined
        resetForm()
        setShowCreate(false)
        await load()
        if (created?.id) nav(`/settings/roles/${created.id}`)
      } catch (e: any) {
        const msg =
          e?.response?.data?.message || e?.response?.data?.error || 'Could not create role.'
        setErr(typeof msg === 'string' ? msg : 'Could not create role.')
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <>
      <PageTitle breadcrumbs={[]}>Roles &amp; permissions</PageTitle>
      <div className='card'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Roles</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              System roles are built-in; custom roles can be tuned for your team.
            </span>
          </div>
          {canCreate ? (
            <div className='card-toolbar'>
              <button type='button' className='btn btn-primary' onClick={() => setShowCreate(true)}>
                Create role
              </button>
            </div>
          ) : null}
        </div>
        <KTCardBody className='py-4'>
          {loading ? (
            <div className='text-center py-10'>Loading…</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Type</th>
                    <th className='text-end'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className='fw-semibold text-gray-800'>{r.name}</td>
                      <td>
                        <code>{r.code}</code>
                      </td>
                      <td>{r.is_system ? 'System' : 'Custom'}</td>
                      <td className='text-end'>
                        {canEdit ? (
                          <button
                            type='button'
                            className='btn btn-sm btn-light-primary'
                            onClick={() => nav(`/settings/roles/${r.id}`)}
                          >
                            {r.code === 'super_admin' ? 'View' : 'Edit'}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </KTCardBody>
      </div>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {err ? <div className='alert alert-danger'>{err}</div> : null}
          <form onSubmit={formik.handleSubmit} noValidate>
            <div className='mb-4'>
              <label className='form-label required'>Code (slug)</label>
              <input className='form-control' {...formik.getFieldProps('code')} />
              {formik.touched.code && formik.errors.code && (
                <div className='text-danger fs-7 mt-1'>{formik.errors.code}</div>
              )}
            </div>
            <div className='mb-4'>
              <label className='form-label required'>Display name</label>
              <input className='form-control' {...formik.getFieldProps('name')} />
            </div>
            <div className='mb-4'>
              <label className='form-label'>Description</label>
              <textarea
                className='form-control'
                rows={2}
                {...formik.getFieldProps('description')}
              />
            </div>
            <div className='d-flex justify-content-end gap-2'>
              <button type='button' className='btn btn-light' onClick={() => setShowCreate(false)}>
                Cancel
              </button>
              <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting}>
                Create
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default RolesWrapper
